<template>
  <div>
    <b-modal
      :id="modalId"
      v-model="localShow"
      size="lg"
      hide-footer
      header-class="preview-modal-header-cls px-4"
      body-class="pt-3 pb-4 px-4"
      dialog-class="preview-modal-dialog"
      no-enforce-focus
      no-fade
      @shown="onModalShown"
      @hidden="onModalHidden"
    >
      <template #modal-header>
        <div
          class="preview-drag-handle"
          @mousedown="onHeaderMousedown"
        >
          <div class="d-flex align-items-center" style="gap: 7px; pointer-events: none; min-width: 0;">
            <span class="drag-grip-icon" title="Drag to move">⠿</span>
            <span class="preview-modal-title-text" @mousedown.stop>{{ modalTitle }}</span>
          </div>
          <div class="preview-modal-header-actions" @mousedown.stop>
            <button
              class="preview-action-btn preview-download-btn"
              :title="isDownloading ? 'Exporting…' : 'Download Excel'"
              :disabled="!filteredRows.length || isDownloading"
              @click.stop="downloadExcel"
            >
              <span v-if="isDownloading" class="preview-btn-spinner" />
              <BaseIcon v-else icon="download" tag="i" :font-scale="0.85" />
            </button>
            <button
              class="preview-action-btn preview-close-btn"
              title="Close"
              @click.stop="closeModal"
            >
              &times;
            </button>
          </div>
        </div>
      </template>

      <div v-if="isFetching" class="d-flex align-items-center py-4">
        <b-spinner small class="mr-2" />
        Loading...
      </div>

      <div v-else-if="!displayRows.length" class="text-muted py-4">
        No preview data available.
      </div>

      <div v-else>
        <!-- Active filter chips + clear-all button -->
        <div v-if="hasActiveFilters" class="filter-chips-bar mb-2">
          <div class="filter-chips">
            <span
              v-for="col in activeFilterCols"
              :key="col"
              class="filter-chip"
            >
              <span class="filter-chip-label">{{ formatColumnName(col) }}: {{ columnFilters[col] }}</span>
              <button class="filter-chip-remove" @click="removeFilter(col)">&times;</button>
            </span>
          </div>
          <button class="clear-all-btn" @click="clearFilters">Clear all</button>
        </div>

        <!-- No-results empty state (shown instead of table when filter matches nothing) -->
        <div v-if="!filteredRows.length" class="preview-empty-state">
          <div class="preview-empty-icon">&#8709;</div>
          <div class="preview-empty-msg">No records match the current filters</div>
          <button class="preview-empty-clear-btn" @click="clearFilters">Clear all filters</button>
        </div>

        <!-- Table (only rendered when there are matching rows) -->
        <div v-else class="preview-scroll">
          <table class="preview-table">
            <thead>
              <tr class="preview-header-row">
                <th v-for="col in tableColumns" :key="col" class="preview-th">
                  <div class="th-inner">
                    <span class="col-label">{{ formatColumnName(col) }}</span>
                    <span
                      class="col-search-icon"
                      :class="{ active: !!(columnFilters[col] || '').trim() || activeSearchCol === col }"
                      :title="isSelectColumn(col) ? 'Filter by status' : 'Filter column'"
                      role="button"
                      tabindex="0"
                      @click.stop="openSearchDropdown(col, $event)"
                      @keydown.enter.stop="openSearchDropdown(col, $event)"
                    >
                      <BaseIcon icon="search" tag="i" :font-scale="0.75" />
                    </span>
                    <span
                      class="col-sort-icon"
                      :class="{ active: sortCol === col }"
                      :title="sortCol === col && sortDir === 'asc' ? 'Sort descending' : 'Sort ascending'"
                      role="button"
                      tabindex="0"
                      @click.stop="toggleSort(col)"
                      @keydown.enter.stop="toggleSort(col)"
                    >
                      <span class="sort-arrows">
                        <span :class="['sort-arrow', 'sort-asc', sortCol === col && sortDir === 'asc' ? 'sort-arrow--active' : '']">▲</span>
                        <span :class="['sort-arrow', 'sort-desc', sortCol === col && sortDir === 'desc' ? 'sort-arrow--active' : '']">▼</span>
                      </span>
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in sortedRows"
                :key="index"
                class="preview-data-row"
              >
                <td v-for="col in tableColumns" :key="col" class="preview-td">
                  {{ formatCellValue(row[col]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Floating search/select dropdown -->
        <div
          v-if="activeSearchCol"
          class="col-search-floating"
          :style="searchDropdownStyle"
          @click.stop
        >
          <template v-if="isSelectColumn(activeSearchCol)">
            <b-form-select
              v-model="pendingFilterValue"
              :options="getSelectOptionsFormatted(activeSearchCol)"
              size="sm"
            />
            <button class="col-search-apply-btn" style="margin-top:8px;" @click="applySelectFilter(activeSearchCol, pendingFilterValue)">
              APPLY
            </button>
          </template>
          <template v-else>
            <textarea
              ref="floating-search-input"
              v-model="pendingFilterValue"
              class="col-search-input-floating"
              rows="3"
              :placeholder="`Filter by ${formatColumnName(activeSearchCol)}\nSeparate multiple values with commas`"
              @keydown.esc.stop="closeSearchDropdown(true)"
              @keydown.enter.exact.stop="closeSearchDropdown()"
            ></textarea>
            <button class="col-search-apply-btn" @click="closeSearchDropdown()">
              APPLY
            </button>
          </template>
        </div>

        <div class="preview-footer mt-2">
          <span class="text-muted" style="font-size: 0.8rem;">
            {{ filteredRows.length }} of {{ displayRows.length }} record{{ displayRows.length === 1 ? '' : 's' }} shown
          </span>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  watch,
  reactive,
  nextTick,
  onUnmounted
} from '@vue/composition-api'

import Vue from 'vue'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

import { extractBdxRows } from '@util'
import { LincolnGlobalUMRService } from '@services'
import { useCurrentUser, useToast, useTryCatch } from '@composables'

// Session-level cache keyed by UMR
const previewCache = new Map()

import { BaseIcon } from '@components/base'

const PAID_MONTH_KEYS = [
  'Paid_This_Month_Indemnity',
  'Paid_This_Month_Fees',
  'Paid_this_month_Expenses',
  'Paid_this_month_Attorney_Coverage_Fees',
  'Paid_this_month_Adjusters_Fees',
  'Paid_this_month_Defence_Fees',
  'Paid_this_month_TPA_Fees',
  'Paid_this_month_Bank_Charges'
]

const RESERVE_KEYS = [
  'Reserve_Expenses',
  'Reserve_Attorney_Coverage_Fees',
  'Reserve_Adjusters_Fees',
  'Reserve_Defence_Fees',
  'Reserve_TPA_Fees',
  'Reserve_Indemnity',
  'Reserve_Fees',
  'Initial_Reserve_Value'
]

const STATUS_PREVIEW_COLUMNS = [
  { key: 'UMR_Risk_Cat_Section', label: 'LimeIQ Key' },
  { key: 'Claim_Reference',      label: 'Claim Reference' },
  { key: 'Claim_Status',         label: 'Status' },
  { key: '_total_paid_month',    label: 'Total Paid This Month' },
  { key: '_total_reserve',       label: 'Total Reserve' },
]

const STATUS_PREVIEW_LABEL_MAP = Object.fromEntries(
  STATUS_PREVIEW_COLUMNS.map(({ key, label }) => [key, label])
)

const SELECT_COLUMN_OPTIONS = {
  Claim_Status: ['Open', 'Closed', 'Reopened', 'F', 'A', 'P', 'File Transferred']
}

// Maps each dropdown label to all raw DB values it should match (exact)
const SELECT_COLUMN_VALUE_MAP = {
  Claim_Status: {
    'Open':             ['Open', 'O'],
    'Closed':           ['Closed', 'C'],
    'Reopened':         ['Reopened', 'Reopen'],
    'F':                ['F'],
    'A':                ['A'],
    'P':                ['P'],
    'File Transferred': ['File Transferred']
  }
}

const MODAL_W = 720
const INITIAL_H = 520

const getInitialPos = () => ({
  top: Math.max(40, Math.round((window.innerHeight - INITIAL_H) / 3)),
  left: Math.max(20, Math.round((window.innerWidth - MODAL_W) / 2))
})
const MIN_W = 320
const MIN_H = 300
const RESIZE_DIRS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw']
// header (~40px) + body padding top+bottom (~40px) + footer (~29px) + buffer
const SCROLL_OVERHEAD = 120

export default defineComponent({
  name: 'BDXStatusPendingPreviewModal',
  components: { BaseIcon },
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: null }
  },
  emits: ['input'],
  setup(props, { emit, refs }) {
    const modalId = 'bdx-status-preview-modal'
    const { userEmail } = useCurrentUser()
    const { showToast } = useToast()

    const localShow = computed({
      get: () => props.value,
      set: (v) => emit('input', v)
    })

    const modalTitle = computed(() => {
      if (!props.item) return 'Preview'
      const coverholder = (props.item.coverholder && props.item.coverholder.coverholder) || ''
      const suffix = coverholder ? `, ${coverholder}` : ''
      return `Preview — ${props.item.umr || ''}${suffix}`
    })

    // ── Helpers ──────────────────────────────────────────────────
    const getDialogEl = () => document.querySelector(`#${modalId} .modal-dialog`)
    const getContentEl = () => document.querySelector(`#${modalId} .modal-content`)

    // ── Position & size state ────────────────────────────────────
    const dragPos = ref(getInitialPos())

    // ── Resize handles (body-level, bypasses z-index/overflow) ──
    const handleEls = []

    const CURSORS = {
      n: 'n-resize', s: 's-resize', e: 'e-resize', w: 'w-resize',
      ne: 'ne-resize', nw: 'nw-resize', se: 'se-resize', sw: 'sw-resize'
    }

    const updateHandlePositions = () => {
      if (!handleEls.length) return
      const dialog = getDialogEl()
      if (!dialog) return
      const r = dialog.getBoundingClientRect()
      const S = 10  // edge handle thickness (px)
      const C = 16  // corner handle size (px)
      const rects = {
        // Edge handles placed just inside the border so grip indicators appear inside
        n:  { top: r.top,            left: r.left + C,      width: r.width - C * 2, height: S },
        s:  { top: r.bottom - S,     left: r.left + C,      width: r.width - C * 2, height: S },
        e:  { top: r.top + C,        left: r.right - S,     width: S,               height: r.height - C * 2 },
        w:  { top: r.top + C,        left: r.left,          width: S,               height: r.height - C * 2 },
        // Corner handles overlap the corner area of the border
        ne: { top: r.top,            left: r.right - C,     width: C,               height: C },
        nw: { top: r.top,            left: r.left,          width: C,               height: C },
        se: { top: r.bottom - C,     left: r.right - C,     width: C,               height: C },
        sw: { top: r.bottom - C,     left: r.left,          width: C,               height: C }
      }
      handleEls.forEach(h => {
        const p = rects[h.dataset.resizeDir]
        if (!p) return
        h.style.top    = p.top + 'px'
        h.style.left   = p.left + 'px'
        h.style.width  = p.width + 'px'
        h.style.height = p.height + 'px'
      })
    }

    const removeResizeHandles = () => {
      handleEls.forEach(h => { if (h.parentNode) h.parentNode.removeChild(h) })
      handleEls.length = 0
    }

    const makeGripLines = (dir) => {
      const grip = document.createElement('div')
      // horizontal border (top/bottom) → horizontal lines stacked (—  —)
      // vertical border (left/right)   → vertical lines side by side (| |)
      const isHBorder = dir === 'n' || dir === 's'
      const isVBorder = dir === 'e' || dir === 'w'
      if (!isHBorder && !isVBorder) return grip  // corners: no lines

      grip.style.cssText = [
        'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);',
        'display:flex;align-items:center;justify-content:center;pointer-events:none;',
        isHBorder ? 'flex-direction:column;gap:3px;' : 'flex-direction:row;gap:3px;'
      ].join('')

      for (let i = 0; i < 2; i++) {
        const line = document.createElement('div')
        // horizontal border → wide+thin bars; vertical border → narrow+tall bars
        line.style.cssText = isHBorder
          ? 'width:14px;height:2px;background:rgba(0,0,0,0.28);border-radius:2px;transition:background 0.12s;'
          : 'width:2px;height:14px;background:rgba(0,0,0,0.28);border-radius:2px;transition:background 0.12s;'
        grip.appendChild(line)
      }
      return grip
    }

    const injectResizeHandles = () => {
      removeResizeHandles()
      RESIZE_DIRS.forEach(dir => {
        const h = document.createElement('div')
        h.dataset.resizeDir = dir
        h.style.cssText = `position:fixed;z-index:10001;cursor:${CURSORS[dir]};transition:background 0.12s;`

        const grip = makeGripLines(dir)
        h.appendChild(grip)

        h.addEventListener('mouseenter', () => {
          h.style.background = 'rgba(0,0,0,0.06)'
          grip.querySelectorAll('div').forEach(l => { l.style.background = 'rgba(0,0,0,0.55)' })
        })
        h.addEventListener('mouseleave', () => {
          h.style.background = ''
          grip.querySelectorAll('div').forEach(l => { l.style.background = 'rgba(0,0,0,0.28)' })
        })

        h.addEventListener('mousedown', (e) => onResizeMousedown(e, dir))
        document.body.appendChild(h)
        handleEls.push(h)
      })
      requestAnimationFrame(() => updateHandlePositions())
    }

    const getScrollEl = () => document.querySelector(`#${modalId} .preview-scroll`)

    const resetScrollEl = () => {
      const scrollEl = getScrollEl()
      if (!scrollEl) return
      scrollEl.style.height = ''
      scrollEl.style.maxHeight = ''
    }

    const applyLayout = (top, left, width, height) => {
      const dialog = getDialogEl()
      const content = getContentEl()
      if (!dialog) return
      dialog.style.margin = '0'
      dialog.style.position = 'fixed'
      dialog.style.top = `${top}px`
      dialog.style.left = `${left}px`
      if (width != null) {
        dialog.style.width = `${width}px`
        dialog.style.maxWidth = `${width}px`
      }
      if (height != null && content) {
        content.style.height = `${height}px`
        content.style.maxHeight = 'none'
        const filterEl = document.querySelector(`#${modalId} .filter-chips`)
        const filterH = filterEl ? filterEl.offsetHeight + 8 : 0
        const scrollEl = getScrollEl()
        if (scrollEl) {
          const scrollH = Math.max(80, height - SCROLL_OVERHEAD - filterH)
          scrollEl.style.height = `${scrollH}px`
          scrollEl.style.maxHeight = 'none'
        }
      }
      dragPos.value = { top, left }
      updateHandlePositions()
    }

    // ── Drag ────────────────────────────────────────────────────
    let dragStartMouse = { x: 0, y: 0 }
    let dragStartPos = { top: 0, left: 0 }

    const onDragMove = (e) => {
      const dx = e.clientX - dragStartMouse.x
      const dy = e.clientY - dragStartMouse.y
      const dialog = getDialogEl()
      const content = getContentEl()
      applyLayout(
        Math.max(0, dragStartPos.top + dy),
        Math.max(0, dragStartPos.left + dx),
        dialog ? parseFloat(dialog.style.width) || null : null,
        content ? parseFloat(content.style.height) || null : null
      )
    }

    const onDragEnd = () => {
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', onDragMove)
      document.removeEventListener('mouseup', onDragEnd)
    }

    const onHeaderMousedown = (e) => {
      if (e.target.closest('button')) return
      e.preventDefault()
      dragStartMouse = { x: e.clientX, y: e.clientY }
      const dialog = getDialogEl()
      if (dialog) {
        const rect = dialog.getBoundingClientRect()
        dragStartPos = { top: rect.top, left: rect.left }
      }
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onDragMove)
      document.addEventListener('mouseup', onDragEnd)
    }

    // ── Resize ───────────────────────────────────────────────────
    let resizeDir = ''
    let resizeStartMouse = { x: 0, y: 0 }
    let resizeStartRect = { top: 0, left: 0, width: 0, height: 0 }

    // Returns the tallest the modal content can be without showing empty space.
    // Uses table.offsetHeight which reflects actual row content regardless of container height.
    const getMaxContentHeight = () => {
      const content = getContentEl()
      if (!content) return Number.MAX_SAFE_INTEGER
      const table = content.querySelector('.preview-table')
      if (!table) return Number.MAX_SAFE_INTEGER
      const filterEl = content.querySelector('.filter-chips')
      const filterH = filterEl ? filterEl.offsetHeight + 8 : 0
      return Math.max(MIN_H, table.offsetHeight + SCROLL_OVERHEAD + filterH)
    }

    const onResizeMove = (e) => {
      const dx = e.clientX - resizeStartMouse.x
      const dy = e.clientY - resizeStartMouse.y
      let { top, left, width, height } = resizeStartRect
      const dir = resizeDir

      if (dir.includes('e')) width = Math.max(MIN_W, width + dx)
      if (dir.includes('w')) {
        const nw = Math.max(MIN_W, width - dx)
        left = left + (width - nw)
        width = nw
      }

      if (dir.includes('s') || dir.includes('n')) {
        // Cap at content's natural height — prevents empty white space
        const maxH = getMaxContentHeight()
        if (dir.includes('s')) height = Math.min(maxH, Math.max(MIN_H, height + dy))
        if (dir.includes('n')) {
          const nh = Math.min(maxH, Math.max(MIN_H, height - dy))
          top = top + (height - nh)
          height = nh
        }
      }

      applyLayout(top, left, width, height)
    }

    const onResizeEnd = () => {
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', onResizeMove)
      document.removeEventListener('mouseup', onResizeEnd)
    }

    const onResizeMousedown = (e, dir) => {
      e.preventDefault()
      e.stopPropagation()
      resizeDir = dir
      resizeStartMouse = { x: e.clientX, y: e.clientY }
      const dialog = getDialogEl()
      const content = getContentEl()
      if (dialog) {
        const r = dialog.getBoundingClientRect()
        resizeStartRect = {
          top: r.top,
          left: r.left,
          width: r.width,
          height: content ? content.getBoundingClientRect().height : r.height
        }
      }
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onResizeMove)
      document.addEventListener('mouseup', onResizeEnd)
    }

    // ── Modal lifecycle ──────────────────────────────────────────
    const onModalShown = () => {
      const pos = getInitialPos()
      dragPos.value = pos
      const dialog = getDialogEl()
      if (dialog) {
        dialog.style.margin = '0'
        dialog.style.position = 'fixed'
        dialog.style.top = `${pos.top}px`
        dialog.style.left = `${pos.left}px`
        dialog.style.width = `${MODAL_W}px`
        dialog.style.maxWidth = `${MODAL_W}px`
      }
      const content = getContentEl()
      if (content) {
        content.style.height = 'auto'
        content.style.maxHeight = `${INITIAL_H}px`
      }
      resetScrollEl()
      // Double rAF: first frame applies styles, second frame reads accurate layout.
      // Opacity is set to 1 here (after position is applied) to prevent the
      // brief flash of the modal at Bootstrap's default position.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (dialog) dialog.style.opacity = '1'
          injectResizeHandles()
        })
      })
    }

    const onModalHidden = () => {
      removeResizeHandles()
      const dialog = getDialogEl()
      if (dialog) {
        dialog.removeAttribute('style')
        dialog.style.opacity = '0'
      }
      const content = getContentEl()
      if (content) {
        content.style.height = ''
        content.style.maxHeight = ''
      }
      resetScrollEl()
    }

    // ── Filters ──────────────────────────────────────────────────
    const isFetching = ref(false)
    const displayRows = ref([])
    const columnFilters = reactive({})

    const tableColumns = computed(() => {
      if (!displayRows.value.length) return []
      const dataKeys = new Set(Object.keys(displayRows.value[0]))
      return STATUS_PREVIEW_COLUMNS
        .filter(({ key }) => dataKeys.has(key))
        .map(({ key }) => key)
    })

    const filteredRows = computed(() =>
      displayRows.value.filter((row) =>
        tableColumns.value.every((col) => {
          const raw = (columnFilters[col] || '').trim()
          if (!raw) return true
          // Select columns: exact match against mapped raw values
          if (isSelectColumn(col) && SELECT_COLUMN_VALUE_MAP[col]) {
            const allowedValues = SELECT_COLUMN_VALUE_MAP[col][raw] || [raw]
            const val = row[col] !== null && row[col] !== undefined ? String(row[col]) : ''
            return allowedValues.includes(val)
          }
          // Text search: split on commas, OR-match with substring
          const terms = raw.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
          if (!terms.length) return true
          const val = row[col] !== null && row[col] !== undefined ? String(row[col]).toLowerCase() : ''
          return terms.some(term => val.includes(term))
        })
      )
    )

    const hasActiveFilters = computed(() =>
      tableColumns.value.some((col) => (columnFilters[col] || '').trim())
    )

    const activeFilterCols = computed(() =>
      tableColumns.value.filter((col) => (columnFilters[col] || '').trim())
    )

    const removeFilter = (col) => { columnFilters[col] = ''; delete filterDrafts[col] }

    const clearFilters = () => {
      tableColumns.value.forEach((col) => { columnFilters[col] = ''; delete filterDrafts[col] })
    }

    const formatColumnName = (key) => STATUS_PREVIEW_LABEL_MAP[key] || String(key).replace(/_/g, ' ')

    const formatCellValue = (v) => {
      if (v === null || v === undefined || v === '') return '—'
      if (typeof v === 'number') return v.toFixed(2)
      return v
    }

    // ── Sorting ───────────────────────────────────────────────────
    const sortCol = ref(null)
    const sortDir = ref('asc')

    const toggleSort = (col) => {
      if (sortCol.value === col) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortCol.value = col
        sortDir.value = 'asc'
      }
    }

    const sortedRows = computed(() => {
      if (!sortCol.value) return filteredRows.value
      const col = sortCol.value
      const dir = sortDir.value === 'asc' ? 1 : -1
      return [...filteredRows.value].sort((a, b) => {
        const av = a[col] != null ? a[col] : ''
        const bv = b[col] != null ? b[col] : ''
        if (av === bv) return 0
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
    })

    const isSelectColumn = (col) => col in SELECT_COLUMN_OPTIONS
    const getSelectOptions = (col) => SELECT_COLUMN_OPTIONS[col] || []
    const getSelectOptionsFormatted = (col) => [
      { value: '', text: `— All ${STATUS_PREVIEW_LABEL_MAP[col] || col} —` },
      ...(SELECT_COLUMN_OPTIONS[col] || []).map((o) => ({ value: o, text: o }))
    ]

    // ── Floating search dropdown ──────────────────────────────────
    const activeSearchCol = ref(null)
    const searchDropdownStyle = ref({})
    const pendingFilterValue = ref('')

    // Non-reactive draft store: saves in-progress text per column when the
    // dropdown is dismissed without APPLY so it can be restored on reopen.
    const filterDrafts = {}

    const openSearchDropdown = async (col, event) => {
      if (activeSearchCol.value === col) { activeSearchCol.value = null; return }
      const rect = event.currentTarget.getBoundingClientRect()
      const dropdownWidth = 228
      const spaceRight = window.innerWidth - rect.right - 8
      const left = spaceRight >= dropdownWidth ? rect.right + 4 : rect.left - dropdownWidth - 4
      searchDropdownStyle.value = {
        top: `${rect.top}px`,
        left: `${Math.max(8, left)}px`
      }
      activeSearchCol.value = col
      // Restore draft if the user previously closed without applying,
      // otherwise fall back to the committed filter value.
      pendingFilterValue.value = col in filterDrafts ? filterDrafts[col] : (columnFilters[col] || '')
      await nextTick()
      const inputEl = refs['floating-search-input']
      const el = Array.isArray(inputEl) ? inputEl[0] : inputEl
      if (el) {
        el.focus()
        // Place cursor at end so user can continue typing after the comma
        el.setSelectionRange(el.value.length, el.value.length)
      }
    }

    const closeSearchDropdown = (cancel = false) => {
      const col = activeSearchCol.value
      if (!cancel && col) {
        // APPLY / Enter: commit the value and discard any draft
        Vue.set(columnFilters, col, pendingFilterValue.value)
        delete filterDrafts[col]
      } else if (cancel && col && pendingFilterValue.value !== (columnFilters[col] || '')) {
        // Dismissed without applying: save as draft so it survives reopen
        filterDrafts[col] = pendingFilterValue.value
      }
      activeSearchCol.value = null
      pendingFilterValue.value = ''
    }

    const applySelectFilter = (col, value) => {
      Vue.set(columnFilters, col, value)
      delete filterDrafts[col]
      activeSearchCol.value = null
      pendingFilterValue.value = ''
    }

    // The floating dropdown has @click.stop so clicks inside it never reach here.
    // Any other click (inside modal or outside) closes the dropdown and saves a draft.
    const onDocClick = () => closeSearchDropdown(true)

    watch(activeSearchCol, (col) => {
      if (col) document.addEventListener('click', onDocClick)
      else document.removeEventListener('click', onDocClick)
    })

    // ── Data loading ──────────────────────────────────────────────
    const initFilterKeys = (rows) => {
      if (!rows.length) return
      Object.keys(rows[0]).forEach((col) => {
        if (!(col in columnFilters)) Vue.set(columnFilters, col, '')
      })
    }

    const enrichRows = (rows) => rows.map((row) => ({
      ...row,
      _total_paid_month: PAID_MONTH_KEYS.reduce((sum, k) => sum + (typeof row[k] === 'number' ? row[k] : 0), 0),
      _total_reserve: RESERVE_KEYS.reduce((sum, k) => sum + (typeof row[k] === 'number' ? row[k] : 0), 0)
    }))

    const loadData = async () => {
      if (!props.item) return
      const cacheKey = props.item.umr_rc_cc_sn || ''
      if (!cacheKey) return
      clearFilters()
      if (previewCache.has(cacheKey)) {
        displayRows.value = previewCache.get(cacheKey)
        initFilterKeys(displayRows.value)
        return
      }
      isFetching.value = true
      displayRows.value = []
      try {
        const response = await LincolnGlobalUMRService.getBdxFullPreview({
          umr_rc_cc_sn: cacheKey
        })
        const rows = enrichRows(extractBdxRows(response.data))
        if (rows.length) previewCache.set(cacheKey, rows)
        displayRows.value = rows
        initFilterKeys(rows)
      } catch {
        displayRows.value = []
      } finally {
        isFetching.value = false
      }
    }

    watch(() => props.value, (newVal) => {
      if (newVal) loadData()
      else {
        clearFilters()
        displayRows.value = []
        activeSearchCol.value = null
      }
    })

    // After data loads: reset to auto-height so content fits (no empty space),
    // then reposition handles to match the new modal size.
    watch([isFetching, displayRows], ([fetching]) => {
      if (!localShow.value) return
      if (!fetching) {
        // Reset explicit height so modal shrinks/grows to fit the new content
        const content = getContentEl()
        if (content) {
          content.style.height = 'auto'
          content.style.maxHeight = `${INITIAL_H}px`
          resetScrollEl()
        }
      }
      if (handleEls.length) {
        nextTick(() => requestAnimationFrame(() => updateHandlePositions()))
      }
    })

    // Keep modal height in sync with filtered results:
    // - no results → remove resize handles, collapse to compact empty state
    // - results present → always reset to auto-height so the modal fits the current
    //   row count (prevents empty space when going from many rows → few rows),
    //   then re-inject or reposition handles as needed.
    watch(filteredRows, (rows) => {
      if (!localShow.value) return
      nextTick(() => {
        const content = getContentEl()
        if (!rows.length && hasActiveFilters.value) {
          // No results — remove handles and reset to auto-height
          removeResizeHandles()
          if (content) {
            content.style.height = 'auto'
            content.style.maxHeight = `${INITIAL_H}px`
            resetScrollEl()
          }
        } else if (rows.length) {
          // Results present — always reset height so modal fits current content
          if (content) {
            content.style.height = 'auto'
            content.style.maxHeight = `${INITIAL_H}px`
            resetScrollEl()
          }
          requestAnimationFrame(() => {
            if (!handleEls.length) {
              injectResizeHandles()
            } else {
              updateHandlePositions()
            }
          })
        }
      })
    })

    const isDownloading = ref(false)

    const downloadExcel = async () => {
      if (isDownloading.value) return
      isDownloading.value = true

      await useTryCatch(async () => {
        const headers = tableColumns.value.map((key) => STATUS_PREVIEW_LABEL_MAP[key] || key)
        const rows = filteredRows.value.map((row) =>
          tableColumns.value.map((key) => {
            const v = row[key]
            return v !== null && v !== undefined ? v : ''
          })
        )
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Preview')
        const now = new Date()
        const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`
        const filename = `${props.item ? props.item.umr || 'preview' : 'preview'} ${ts}.xlsx`
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename)

        if (props.item) {
          await LincolnGlobalUMRService.exportPreview(
            props.item.umr_rc_cc_sn || '',
            userEmail.value
          )
          showToast('The preview export is in process, and you will receive an update via email shortly!')
        }
      })

      isDownloading.value = false
    }

    const closeModal = () => {
      if (document.activeElement) document.activeElement.blur()
      localShow.value = false
    }

    onUnmounted(() => {
      removeResizeHandles()
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('mousemove', onDragMove)
      document.removeEventListener('mouseup', onDragEnd)
      document.removeEventListener('mousemove', onResizeMove)
      document.removeEventListener('mouseup', onResizeEnd)
    })

    return {
      modalId,
      localShow,
      modalTitle,
      closeModal,
      onModalShown,
      onModalHidden,
      onHeaderMousedown,
      isFetching,
      displayRows,
      columnFilters,
      tableColumns,
      filteredRows,
      hasActiveFilters,
      activeFilterCols,
      removeFilter,
      clearFilters,
      formatColumnName,
      formatCellValue,
      sortCol,
      sortDir,
      toggleSort,
      sortedRows,
      isSelectColumn,
      getSelectOptions,
      getSelectOptionsFormatted,
      applySelectFilter,
      activeSearchCol,
      searchDropdownStyle,
      pendingFilterValue,
      openSearchDropdown,
      closeSearchDropdown,
      isDownloading,
      downloadExcel
    }
  }
})
</script>

<style>
.preview-modal-dialog {
  max-width: 720px;
  position: fixed !important;
  margin: 0 !important;
  overflow: visible;
  opacity: 0;
  transition: opacity 0.12s ease;
}

.preview-modal-header-cls {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  border-bottom: 1px solid #dee2e6;
}

#bdx-status-preview-modal .modal-content {
  display: flex;
  flex-direction: column;
  border: 2px solid #b8cfe0 !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18) !important;
}

#bdx-status-preview-modal .modal-body {
  overflow: visible;
  /* Prevent Bootstrap's flex:1 1 auto from stretching modal-body beyond its
     content — otherwise an explicit modal-content height leaves empty space
     above the filter chips when filtered rows are fewer than the full set. */
  flex: 0 0 auto;
}


/* ── Floating search dropdown ────────────────────────────── */
.col-search-floating {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 10px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.col-search-input-floating {
  display: block;
  width: 100%;
  font-size: 0.85rem;
  padding: 5px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  outline: none;
  color: #495057;
  background: #fff;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  line-height: 1.4;
}
.col-search-input-floating:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
}
.col-search-input-floating::placeholder { color: #adb5bd; }

.col-search-apply-btn {
  align-self: flex-start;
  background: #1a3a4a;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 14px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background 0.15s;
}
.col-search-apply-btn:hover { background: #0f2530; }
</style>

<style scoped>
/* ── Drag handle ─────────────────────────────────────────── */
.preview-drag-handle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: grab;
  user-select: none;
}
.preview-drag-handle:active { cursor: grabbing; }

.drag-grip-icon {
  font-size: 1.15rem;
  color: #adb5bd;
  line-height: 1;
  pointer-events: none;
  flex-shrink: 0;
  letter-spacing: -1px;
}

.preview-modal-title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
  pointer-events: auto;
  cursor: text;
  user-select: text;
}

.preview-modal-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: default;
}

.preview-action-btn {
  background: none;
  border: none;
  outline: none;
  padding: 2px 7px;
  font-size: 1.1rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.12s, color 0.12s;
}
.preview-action-btn:focus { outline: none; box-shadow: none; }
.preview-action-btn:hover { background: #f0f0f0; color: #212529; }
.preview-close-btn:hover { background: #fee2e2; color: #dc3545; }
.preview-download-btn:hover { background: #e8f4fd; color: #0056b3; }
.preview-download-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.preview-btn-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(0, 123, 255, 0.25);
  border-top-color: #007bff;
  border-radius: 50%;
  animation: preview-spin 0.7s linear infinite;
}
@keyframes preview-spin { to { transform: rotate(360deg); } }

/* ── Filter chips bar ────────────────────────────────────── */
.filter-chips-bar {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
}

/* min-width forces the chips row to wrap below Clear all before chips
   get so narrow that they visually overlap the button */
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 140px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #28a745;
  border-radius: 20px;
  padding: 3px 10px 3px 12px;
  font-size: 0.78rem;
  color: #155724;
  background: #fff;
  min-width: 0;
  max-width: 260px;
  overflow: hidden;
}

.filter-chip-label {
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-chip-remove {
  background: none;
  border: none;
  padding: 0;
  line-height: 1;
  font-size: 1rem;
  color: #dc3545;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.filter-chip-remove:hover { color: #a71d2a; }

.clear-all-btn {
  flex-shrink: 0;
  align-self: flex-start;
  background: none;
  border: 1px solid #adb5bd;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 0.75rem;
  color: #6c757d;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.12s, color 0.12s;
}
.clear-all-btn:hover { border-color: #dc3545; color: #dc3545; }

/* ── No-results empty state ──────────────────────────────── */
.preview-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 8px;
  text-align: center;
}

.preview-empty-icon {
  font-size: 2rem;
  color: #ced4da;
  line-height: 1;
}

.preview-empty-msg {
  font-size: 0.875rem;
  color: #6c757d;
}

.preview-empty-clear-btn {
  margin-top: 4px;
  background: none;
  border: 1px solid #adb5bd;
  border-radius: 4px;
  padding: 4px 14px;
  font-size: 0.8rem;
  color: #495057;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s;
}
.preview-empty-clear-btn:hover { border-color: #0056b3; color: #0056b3; }

/* ── Table ───────────────────────────────────────────────── */
.preview-scroll {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 400px; /* CSS cap for auto-height mode; overridden by JS when resizing */
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  min-width: max-content;
}

.preview-th {
  padding: 8px 14px 6px;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  vertical-align: top;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.th-inner { display: flex; align-items: center; gap: 5px; }

.col-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  white-space: nowrap;
}

.col-search-icon {
  cursor: pointer;
  color: #adb5bd;
  line-height: 1;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border-radius: 3px;
  transition: color 0.15s;
}
.col-search-icon:hover,
.col-search-icon.active { color: #007bff; }

.col-sort-icon {
  cursor: pointer;
  color: #adb5bd;
  line-height: 1;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border-radius: 3px;
  transition: color 0.15s;
}
.col-sort-icon:hover,
.col-sort-icon.active { color: #007bff; }

.sort-arrows {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  line-height: 1;
}

.sort-arrow {
  font-size: 0.45rem;
  color: #ced4da;
  line-height: 1;
  display: block;
}
.sort-arrow--active { color: #007bff; }

.preview-data-row { transition: background 0.1s; }
.preview-data-row:not(:last-child) .preview-td { border-bottom: 1px solid #f0f0f0; }
.preview-data-row:hover { background-color: #f8f9fa; }

.preview-td {
  padding: 10px 14px;
  font-size: 0.875rem;
  white-space: nowrap;
  vertical-align: middle;
}

.preview-footer { display: flex; align-items: center; }
</style>
